import type { ReactNode } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Settings, ArrowLeft, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConfig } from '@/api/config';
import ThemeToggle from './ThemeToggle';

interface Tab {
  to: string;
  label: string;
  // When set and true, the tab renders as non-clickable greyed-out
  // text with a tooltip explaining why. The user explicitly asked for
  // disabled-not-invisible behavior so admins still discover the
  // feature exists but is turned off in this deployment.
  disabled?: boolean;
  disabledReason?: string;
}

export default function AppShell({ children }: { children: ReactNode }) {
  const { data: config } = useConfig();

  // Default to enabled while config is loading so we don't briefly
  // grey out a tab on first render. Once data arrives, the flag wins.
  const licensesDisabled = config ? !config.licenseManagerEnabled : false;

  const tabs: Tab[] = [
    { to: '/resources', label: 'Resources' },
    { to: '/quotas', label: 'Quotas' },
    {
      to: '/licenses',
      label: 'Licenses',
      disabled: licensesDisabled,
      disabledReason:
        'License Manager is not enabled on this server (set RM_LICENSE_MANAGER_ENABLED=true to turn it on).',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-4 h-14">
          <Link
            to="/resources"
            title="Go to the main page"
            className="flex items-center gap-3 shrink-0 rounded-sm hover:opacity-80 transition-opacity"
          >
            <span
              className="text-xl font-extrabold tracking-tight leading-none select-none"
              aria-label="SIMR"
            >
              <span className="text-foreground">SIM</span>
              <span className="text-destructive">R</span>
            </span>
            <span className="hidden md:inline text-xl font-medium leading-none text-muted-foreground">
              Resource Manager
            </span>
          </Link>

          <nav
            className="flex-1 flex justify-center"
            role="navigation"
            aria-label="Primary"
          >
            <ul className="flex">
              {tabs.map((t) => (
                <li key={t.to}>
                  {t.disabled ? (
                    <span
                      role="link"
                      aria-disabled="true"
                      title={t.disabledReason}
                      className={cn(
                        'flex h-14 items-center justify-center w-[140px] border-b-2 text-sm font-medium',
                        'border-transparent text-muted-foreground/50 cursor-not-allowed select-none',
                      )}
                    >
                      {t.label}
                    </span>
                  ) : (
                    <NavLink
                      to={t.to}
                      className={({ isActive }) =>
                        cn(
                          'flex h-14 items-center justify-center w-[140px] border-b-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'border-primary text-foreground'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50 hover:border-muted-foreground/40',
                        )
                      }
                    >
                      {t.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <a
            href="/"
            className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="Return to the classic UI"
          >
            <ArrowLeft className="h-3 w-3" />
            Classic UI
          </a>

          <ThemeToggle />

          <NavLink
            to="/server-activity"
            className={({ isActive }) =>
              cn(
                'p-2 rounded-md hover:bg-muted transition-colors',
                isActive && 'bg-muted text-foreground',
              )
            }
            aria-label="Server Activity"
            title="Server Activity"
          >
            <Activity className="h-5 w-5" />
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              cn(
                'p-2 rounded-md hover:bg-muted transition-colors',
                isActive && 'bg-muted text-foreground',
              )
            }
            aria-label="Settings"
          >
            <Settings className="h-5 w-5" />
          </NavLink>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6">{children}</main>

      <footer className="border-t py-4 text-center text-xs text-muted-foreground">
        © TheUberCloud, Inc. doing business as Simr
      </footer>
    </div>
  );
}
